// Data access. Every query runs as the signed-in user, so row-level security in the database
// decides what comes back; progress changes go through database functions that validate them.
import { supabase } from './supabase';
import type { Certificate, Chapter, Course, Enrollment, EnrollmentProgress, Lesson, LessonBlock, LessonProgress, Profile } from './types';

function check<T>(res: { data: T | null; error: unknown }): T {
  if (res.error) throw res.error;
  return res.data as T;
}

export async function listPublishedCourses(): Promise<Course[]> {
  return check(await supabase.from('courses').select('*').eq('status', 'published').order('title'));
}

export async function getCourseBySlug(slug: string): Promise<Course | null> {
  return check(await supabase.from('courses').select('*').eq('slug', slug).maybeSingle());
}

export async function getCurriculum(courseId: string): Promise<{ chapters: Chapter[]; lessons: Lesson[] }> {
  const [ch, ls] = await Promise.all([
    supabase.from('chapters').select('*').eq('course_id', courseId).order('position'),
    supabase.from('lessons').select('*').eq('course_id', courseId).order('position'),
  ]);
  return { chapters: check(ch) as Chapter[], lessons: check(ls) as Lesson[] };
}

export async function getMyEnrollment(courseId: string, userId: string): Promise<Enrollment | null> {
  return check(await supabase.from('enrollments').select('*').eq('course_id', courseId).eq('user_id', userId).maybeSingle());
}

export async function getProgress(enrollmentId: string): Promise<LessonProgress[]> {
  return check(await supabase.from('lesson_progress').select('*').eq('enrollment_id', enrollmentId));
}

export async function getMyDashboard(userId: string): Promise<{ progress: EnrollmentProgress[]; courses: Course[]; lessons: Pick<Lesson, 'id' | 'title' | 'course_id'>[] }> {
  const progress = check(await supabase.from('enrollment_progress').select('*').eq('user_id', userId)) as EnrollmentProgress[];
  const ids = progress.map((p) => p.course_id);
  if (!ids.length) return { progress, courses: [], lessons: [] };
  const lastIds = progress.map((p) => p.last_lesson_id).filter(Boolean) as string[];
  const [courses, lessons] = await Promise.all([
    supabase.from('courses').select('*').in('id', ids),
    lastIds.length ? supabase.from('lessons').select('id,title,course_id').in('id', lastIds) : Promise.resolve({ data: [], error: null }),
  ]);
  return { progress, courses: check(courses) as Course[], lessons: check(lessons) as Pick<Lesson, 'id' | 'title' | 'course_id'>[] };
}

export async function getLessonBlocks(lessonId: string): Promise<LessonBlock[]> {
  return check(await supabase.from('lesson_blocks').select('*').eq('lesson_id', lessonId).order('position'));
}

export async function openLesson(lessonId: string): Promise<LessonProgress> {
  return check(await supabase.rpc('open_lesson', { p_lesson: lessonId }));
}

export async function recordVideoProgress(lessonId: string, positionS: number, durationS: number): Promise<LessonProgress> {
  return check(await supabase.rpc('record_video_progress', { p_lesson: lessonId, p_position_s: positionS, p_duration_s: durationS }));
}

export async function completeLesson(lessonId: string): Promise<LessonProgress> {
  return check(await supabase.rpc('complete_lesson', { p_lesson: lessonId }));
}

export async function listMyCertificates(userId: string): Promise<Certificate[]> {
  return check(await supabase.from('certificates').select('*').eq('user_id', userId).is('revoked_at', null).order('issued_at', { ascending: false }));
}

export async function verifyCertificate(publicId: string) {
  const rows = check(await supabase.rpc('verify_certificate', { p_public_id: publicId })) as
    { recipient_name: string; course_title: string; pdh: number; completed_on: string; issued_at: string; revoked_at: string | null }[];
  return rows[0] ?? null;
}

export async function updateProfile(userId: string, patch: Partial<Profile>): Promise<void> {
  const { id: _ignored, ...safe } = patch;
  check(await supabase.from('profiles').update(safe).eq('id', userId).select().single());
}
